import React, { Dispatch, SetStateAction } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { adjustSales } from '@/apis/aipass'
import { AdjustmentPaymentForm } from '@/components/organisms/sales-manager/_sales/adjustment-payment-form'
import { NOT_ENOUGH_BALANCE_CODE } from '@/errors/company-advance-money-error'
import { PaymentSettingType, SalesDiscountType, SalesType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { AdvanceCompanyType } from '@/models/sales-manager/advance-money'
type AdjustmentProps = {
  totalSalesPrice: number
  totalTax: number
  totalSalesPaymentPrice: number
  totalSalesDiscountPrice: number
  totalUnpaid: number
  totalSalesPayments: { companyId?: any; name: string; totalDiscount: number }[]
  currentTabReservationId?: string
  checkinId?: string
  setIsLoading: Dispatch<SetStateAction<boolean>>
  onAdjustSales: () => void | Promise<void>
  onSendPos: () => void | Promise<void>
  filterGuestRoomAssignId?: string[]
  usageDetails: SalesType[]
  discounts: SalesDiscountType[]
  advanceCompany: AdvanceCompanyType
  salesPayments: PaymentSettingType[]
  accountsReceivableSettings: AccountsReceivableSettingType[]
  isMastersLoaded: boolean
}

export const Adjustment: React.FC<AdjustmentProps> = props => {
  const { t } = useTranslation()
  const { advanceCompany } = props

  const _onAdjustSales = async ({ salesPaymentId, accountsReceivableId, companyId }) => {
    props.setIsLoading(true)

    await adjustSales({
      salesPaymentId,
      saleIds: [...props.usageDetails.map(item => item.id), ...props.discounts.map(item => item.id)],
      companyId,
      salesAccountsReceivableMasterId: accountsReceivableId,
    })
      .then(async () => {
        await props.onAdjustSales()
      })
      .catch(error => {
        if (error?.response?.data?.errorCode === NOT_ENOUGH_BALANCE_CODE) {
          alert(t('Insufficient balance Please check again'))
        }
      })
      .finally(() => props.setIsLoading(false))
  }

  return (
    <div css={salesRightItemStyle}>
      <table css={salesItemStyle}>
        <tbody>
          <tr>
            <td css={headStyle}>{t('Total sales')}</td>
            <td css={rightHeadStyle}>¥{Number(props.totalSalesPrice).toLocaleString()}</td>
          </tr>
          <tr>
            <td css={bodyStyle}>{t('Consumption tax')}</td>
            <td css={rightBodyStyle}>¥{Number(props.totalTax).toLocaleString()}</td>
          </tr>
          <tr>
            <td css={bodyStyle}>{t('Receipt total')}</td>
            <td css={rightBodyStyle}>¥{Number(props.totalSalesPaymentPrice).toLocaleString()}</td>
          </tr>
          <>
            {props.totalSalesPayments &&
              props.totalSalesPayments?.map((totalSalesPayment, index) => (
                <tr key={index}>
                  <td css={discountStyle}>
                    {totalSalesPayment.companyId
                      ? advanceCompany?.paymentCompanies?.find(v => v.id === totalSalesPayment.companyId)?.name
                      : totalSalesPayment.name}
                  </td>
                  <td css={rightBodyStyle}>¥{Number(totalSalesPayment.totalDiscount).toLocaleString()}</td>
                </tr>
              ))}
          </>
          <tr>
            <td css={[discountStyle, bottomStyle]}>{t('Discount')}</td>
            <td css={[rightBodyStyle, bottomStyle]}>¥{Number(props.totalSalesDiscountPrice).toLocaleString()}</td>
          </tr>
          <tr>
            <td css={props.totalUnpaid > 0 ? isUnpaidFootStyle : footStyle}>{t('Amount to be collected')}</td>
            <td css={props.totalUnpaid > 0 ? isUnpaidRightFootStyle : rightFootStyle}>
              {props.totalUnpaid < 0 ? '-' : ''}¥{Math.abs(props.totalUnpaid).toLocaleString()}
            </td>
          </tr>
        </tbody>
      </table>

      <AdjustmentPaymentForm
        checkinId={props.checkinId}
        reservationId={props.currentTabReservationId}
        onAdjustSales={_onAdjustSales}
        onClose={(isUpdated: boolean) => {
          isUpdated && props.onSendPos()
        }}
        filterGuestRoomAssignId={props.filterGuestRoomAssignId}
        advanceCompany={advanceCompany}
        salesPayments={props.salesPayments}
        accountsReceivableSettings={props.accountsReceivableSettings}
        isMastersLoaded={props.isMastersLoaded}
      />
    </div>
  )
}

const salesItemStyle = css({
  fontSize: 14,
  width: '100%',
})

const salesRightItemStyle = css(salesItemStyle, {
  width: '30%',
  minWidth: 256,
  padding: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 5,
  flexDirection: 'column',
  height: 'fit-content',
  marginLeft: 16,
})

const headStyle = css({
  width: '70%',
  color: '#272727',
  fontWeight: 'bold',
  paddingBottom: 16,
})

const bodyStyle = css({
  width: '70%',
  color: '#676767',
  margin: '16px 0',
  paddingBottom: 16,
})

const rightHeadStyle = css(headStyle, {
  textAlign: 'right',
})

const rightBodyStyle = css(bodyStyle, {
  textAlign: 'right',
})

const discountStyle = css(bodyStyle, {
  paddingLeft: 28,
})

const bottomStyle = css({
  paddingBottom: 24,
})

const footStyle = css({
  borderTop: '1px solid #F2F2F2',
  paddingTop: 24,
  fontWeight: 'bold',
})

const isUnpaidFootStyle = css(footStyle, {
  paddingTop: 24,
  fontWeight: 'bold',
})

const isUnpaidRightFootStyle = css(isUnpaidFootStyle, {
  textAlign: 'right',
  color: '#F2A40B',
})

const rightFootStyle = css(footStyle, {
  textAlign: 'right',
})
